import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Hammer, Users, Truck, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { QuickCategory } from "@/types/construction";
import { useCostCenters } from "@/hooks/useCostCenters";
import { useCategories } from "@/hooks/useCategories";
import { usePaymentAccounts } from "@/hooks/usePaymentAccounts";
import { useTransaction } from "@/hooks/useTransaction";
import { toast } from "sonner";

// Quick categories for construction expenses
const QUICK_CATEGORIES: QuickCategory[] = [
  {
    name: "Material de Construção",
    icon: Hammer,
    type: "expense",
    color: "text-orange-600"
  },
  {
    name: "Mão de Obra",
    icon: Users,
    type: "expense",
    color: "text-blue-600"
  },
  {
    name: "Aluguel de Equipamento",
    icon: Truck,
    type: "expense",
    color: "text-green-600"
  },
];

interface AddExpenseFormProps {
  onSuccess?: () => void;
}

export function AddExpenseForm({ onSuccess }: AddExpenseFormProps) {
  const { costCenters, isLoading: isLoadingCostCenters } = useCostCenters();
  const { categories, isLoading: isLoadingCategories } = useCategories();
  const { paymentAccounts, isLoading: isLoadingAccounts } = usePaymentAccounts();
  const { create: createTransaction, isCreating } = useTransaction();

  const [selectedQuickCategory, setSelectedQuickCategory] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    due_date: new Date(),
    cost_center_id: "",
    category_id: "",
    payment_account_id: "",
    supplier_name: "",
    notes: "",
  });

  const activeCostCenters = costCenters?.filter((cc) => cc.is_active) || [];
  const expenseCategories = categories?.filter((c) => c.type === "expense") || [];
  const activePaymentAccounts = paymentAccounts?.filter((pa) => pa.is_active) || [];

  const handleQuickCategoryClick = (categoryName: string) => {
    setSelectedQuickCategory(categoryName);

    // Find matching category in database
    const matchingCategory = expenseCategories.find(
      (cat) => cat.name.toLowerCase().includes(categoryName.toLowerCase())
    );

    if (matchingCategory) {
      setFormData((prev) => ({
        ...prev,
        category_id: matchingCategory.id,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.description.trim()) {
      toast.error("Descrição é obrigatória");
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Valor deve ser maior que zero");
      return;
    }

    if (!formData.cost_center_id) {
      toast.error("Selecione uma obra");
      return;
    }

    if (!formData.payment_account_id) {
      toast.error("Selecione uma conta de pagamento");
      return;
    }

    try {
      await createTransaction({
        type: "expense",
        description: formData.description,
        amount,
        due_date: format(formData.due_date, "yyyy-MM-dd"),
        is_paid: false,
        cost_center_id: formData.cost_center_id,
        category_id: formData.category_id || undefined,
        payment_account_id: formData.payment_account_id,
        supplier_name: formData.supplier_name || undefined,
        notes: formData.notes || undefined,
      });

      toast.success("Custo lançado com sucesso!");

      // Reset form
      setFormData({
        description: "",
        amount: "",
        due_date: new Date(),
        cost_center_id: "",
        category_id: "",
        payment_account_id: "",
        supplier_name: "",
        notes: "",
      });
      setSelectedQuickCategory(null);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error creating expense:", error);
      toast.error("Erro ao lançar custo");
    }
  };

  const isLoading = isLoadingCostCenters || isLoadingCategories || isLoadingAccounts;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando formulário...</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <CardTitle>Lançar Custo na Obra</CardTitle>
          <CardDescription>
            Registre despesas relacionadas a uma obra específica
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Quick Category Selection */}
          <div className="space-y-2">
            <Label>Categoria Rápida</Label>
            <div className="grid grid-cols-3 gap-4">
              {QUICK_CATEGORIES.map((qc) => {
                const Icon = qc.icon;
                const isSelected = selectedQuickCategory === qc.name;
                return (
                  <Button
                    key={qc.name}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleQuickCategoryClick(qc.name)}
                  >
                    <Icon className={cn("h-6 w-6", isSelected ? "" : qc.color)} />
                    <span className="text-sm text-center">{qc.name}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Cost Center (Obra) Selection */}
          <div className="space-y-2">
            <Label htmlFor="cost_center_id">
              Obra <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.cost_center_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, cost_center_id: value }))
              }
            >
              <SelectTrigger id="cost_center_id">
                <SelectValue placeholder="Selecione a obra" />
              </SelectTrigger>
              <SelectContent>
                {activeCostCenters.map((cc) => (
                  <SelectItem key={cc.id} value={cc.id}>
                    {cc.code && `${cc.code.toString().padStart(4, "0")} - `}
                    {cc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">
              Descrição <span className="text-destructive">*</span>
            </Label>
            <Input
              id="description"
              placeholder="Ex: Cimento 50kg, Pedreiro João - 3 dias"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">
              Valor <span className="text-destructive">*</span>
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.amount}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, amount: e.target.value }))
              }
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label>Data de Vencimento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.due_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.due_date ? (
                    format(formData.due_date, "PPP", { locale: ptBR })
                  ) : (
                    "Selecione a data"
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.due_date}
                  onSelect={(date) =>
                    setFormData((prev) => ({ ...prev, due_date: date || new Date() }))
                  }
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Category (Optional - auto-filled by quick category) */}
          <div className="space-y-2">
            <Label htmlFor="category_id">Categoria (Opcional)</Label>
            <Select
              value={formData.category_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, category_id: value }))
              }
            >
              <SelectTrigger id="category_id">
                <SelectValue placeholder="Selecione a categoria" />
              </SelectTrigger>
              <SelectContent>
                {expenseCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Payment Account */}
          <div className="space-y-2">
            <Label htmlFor="payment_account_id">
              Conta de Pagamento <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.payment_account_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, payment_account_id: value }))
              }
            >
              <SelectTrigger id="payment_account_id">
                <SelectValue placeholder="Selecione a conta" />
              </SelectTrigger>
              <SelectContent>
                {activePaymentAccounts.map((pa) => (
                  <SelectItem key={pa.id} value={pa.id}>
                    {pa.name} - {pa.bank_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Supplier Name */}
          <div className="space-y-2">
            <Label htmlFor="supplier_name">Fornecedor (Opcional)</Label>
            <Input
              id="supplier_name"
              placeholder="Nome do fornecedor ou prestador"
              value={formData.supplier_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, supplier_name: e.target.value }))
              }
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Observações (Opcional)</Label>
            <Textarea
              id="notes"
              placeholder="Informações adicionais sobre este custo"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={isCreating}
          >
            <Save className="mr-2 h-4 w-4" />
            {isCreating ? "Salvando..." : "Lançar Custo"}
          </Button>
        </CardContent>
      </Card>
    </form>
  );
}
